import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Email,
  Phone,
  LocationOn,
} from '@mui/icons-material';

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Destinations', href: '#destinations' },
    { name: 'Services', href: '#services' },
    { name: 'Contact', href: '#contact' },
  ];

  const services = [
    'Study Abroad Counseling',
    'Visa Processing',
    'University Applications',
    'IELTS/TOEFL Coaching',
    'Scholarship Guidance',
    'Pre-departure Support',
  ];

  const destinations = [
    'Australia',
    'Canada',
    'United Kingdom',
    'United States',
    'New Zealand',
    'Ireland',
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5aa0 100%)',
        color: 'white',
        py: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ mb: 2 }}>
                <motion.img
                  src="/full_logo.png?v=3"
                  alt="Future Forwards Education"
                  style={{
                    height: '60px',
                    width: 'auto',
                    maxWidth: '250px',
                    borderRadius: '12px',
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: 1.6,
                  mb: 3,
                }}
              >
                Your trusted partner for study abroad opportunities. We help students achieve their dreams of studying in top universities worldwide.
              </Typography>

              {/* Contact Info */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Phone sx={{ fontSize: '1.2rem', mr: 1, color: '#ff6b35' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    +91 7356475308, +91 7736715551
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Email sx={{ fontSize: '1.2rem', mr: 1, color: '#ff6b35' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    info@futureforwards.in
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <LocationOn sx={{ fontSize: '1.2rem', mr: 1, color: '#ff6b35', mt: 0.2 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Trivandrum, Kerala, India
                  </Typography>
                </Box>
              </Box>

            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={2}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Links
              </Typography>
              {quickLinks.map((link, index) => (
                <motion.div
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Link
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    sx={{
                      display: 'block',
                      color: 'rgba(255, 255, 255, 0.8)',
                      textDecoration: 'none',
                      mb: 1,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#ff6b35',
                      },
                    }}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </Grid>

          {/* Services */}
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Our Services
              </Typography>
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 1,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#ff6b35',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    {service}
                  </Typography>
                </motion.div>
              ))}
            </motion.div>
          </Grid>

          {/* Destinations */}
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Popular Destinations
              </Typography>
              {destinations.map((destination, index) => (
                <motion.div
                  key={index}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      mb: 1,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#ff6b35',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    {destination}
                  </Typography>
                </motion.div>
              ))}
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              mt: 4,
              pt: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              © 2025 Future Forwards Education. All rights reserved. |{' '}
              <Link
                href="/privacy"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#ff6b35',
                  },
                }}
              >
                Privacy Policy
              </Link>
              {' | '}
              <Link
                href="/terms"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#ff6b35',
                  },
                }}
              >
                Terms of Service
              </Link>
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;
